import { JstDate } from '../../core/time/business-time';
import { NumericOnlyDirective } from '../../shared/directives/numeric-only.directive';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { PageTitleService } from '../../core/services/page-title.service';
import { StudioService } from '../../core/services/studio.service';
import { BalanceService } from '../../core/services/balance.service';
import { TaxRateService } from '../../core/services/tax-rate.service';
import { AuthStateService } from '../../core/services/auth-state.service';
import { StudioSelectionService } from '../../core/services/studio-selection.service';
import { prepareDetailPdfPages } from './balance-pdf-pages';
import { summarizeBalance, getTaxAmount } from './domain/balance-summary';

const SESSION_KEY = 'balance_select_studio_id';

function buildYearOptions(): { id: number; name: string }[] {
  const now = new JstDate();
  const options: { id: number; name: string }[] = [];
  for (let y = 2015; y <= now.getFullYear() + 1; y++) {
    options.push({ id: y, name: `${y}年` });
  }
  return options;
}

function buildMonthOptions(): { id: number; name: string }[] {
  return Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: `${i + 1}月末締め` }));
}

function getDefaultYear(): number {
  const now = new JstDate();
  return now.getMonth() ? now.getFullYear() : now.getFullYear() - 1;
}

function getDefaultMonth(): number {
  const now = new JstDate();
  return now.getMonth() ? now.getMonth() : 12;
}

function getChargeTypeName(chargeType: number): string {
  const names = ['その他', '月会費', '入会金・事務手数料', '契約ロッカー', 'パーソナルトレーニング', '物販'];
  return names[chargeType || 0] ?? 'その他';
}

function initSettlesData(): any {
  return {
    isContinued: false, plansName: '', chargeType: null, chargeTypeName: '', unitPrice: '',
    totalCount: 0, totalTaxIncAmount: 0,
    btTotalCount: 0, btTotalTaxIncAmount: 0, btTradeCount: 0, btTradeTaxIncAmount: 0, btErrorCount: 0, btErrorTaxIncAmount: 0,
    ccTotalCount: 0, ccTotalTaxIncAmount: 0, ccTradeCount: 0, ccTradeTaxIncAmount: 0, ccErrorCount: 0, ccErrorTaxIncAmount: 0,
  };
}

function pdfArraySort(array: any[]): any[] {
  if (array.length === 0) return [];
  if (array[0].chargeType === 0) {
    return array.sort((a, b) => {
      if (a.chargeTypeName > b.chargeTypeName) return 1;
      if (a.chargeTypeName < b.chargeTypeName) return -1;
      return a.totalCount > b.totalCount ? -1 : a.totalCount < b.totalCount ? 1 : 0;
    });
  }
  return array.sort((a, b) => {
    if (a.chargeType !== b.chargeType) return a.chargeType > b.chargeType ? 1 : -1;
    if (a.plansName && !b.plansName) return -1;
    if (a.plansName !== b.plansName) return a.plansName > b.plansName ? 1 : -1;
    return a.totalCount > b.totalCount ? -1 : a.totalCount < b.totalCount ? 1 : 0;
  });
}

function arrayFillAndSlice(array: any[], count: number): any[][] {
  const blankRow = () => ({ plansName: ' ', isPadding: true });
  if (array.length === 0) return [Array.from({ length: count }, blankRow)];
  const rem = array.length % count;
  let fillArray = rem === 0 ? [...array] : array.concat(new Array(count - rem).fill(null).map(blankRow));
  if (fillArray.some(v => v?.isTotal)) {
    fillArray.push(fillArray.shift()!);
  }
  const pages = Math.ceil(fillArray.length / count);
  return new Array(pages).fill(null).map((_, i) => fillArray.slice(i * count, (i + 1) * count));
}

function sumPdfData(target: any, source: any): void {
  target.totalCount += source.totalCount; target.totalTaxIncAmount += source.totalTaxIncAmount;
  target.btTotalCount += source.btTotalCount; target.btTotalTaxIncAmount += source.btTotalTaxIncAmount;
  target.btTradeCount += source.btTradeCount; target.btTradeTaxIncAmount += source.btTradeTaxIncAmount;
  target.btErrorCount += source.btErrorCount; target.btErrorTaxIncAmount += source.btErrorTaxIncAmount;
  target.ccTotalCount += source.ccTotalCount; target.ccTotalTaxIncAmount += source.ccTotalTaxIncAmount;
  target.ccTradeCount += source.ccTradeCount; target.ccTradeTaxIncAmount += source.ccTradeTaxIncAmount;
  target.ccErrorCount += source.ccErrorCount; target.ccErrorTaxIncAmount += source.ccErrorTaxIncAmount;
}

function summarizeIsContinued(dataArr: any[]): any[] {
  return dataArr.reduce((prev: any[], current: any, index: number) => {
    if (current.totalCount === 0) return prev;
    const data = prev.find(v => v.plansName === current.plansName && v.chargeTypeName === current.chargeTypeName && v.unitPrice === current.unitPrice && !v.isTotal);
    if (index === 0) {
      const total = JSON.parse(JSON.stringify(current));
      total.isTotal = true;
      prev.unshift(total);
      prev.push({ ...current });
      return prev;
    }
    if (current.chargeType === 0 || !data) {
      prev.push({ ...current });
      sumPdfData(prev[0], current);
      return prev;
    }
    sumPdfData(data, current);
    sumPdfData(prev[0], current);
    return prev;
  }, []);
}

function createTotalData(dataArr: any[]): any[] {
  const data = pdfArraySort(summarizeIsContinued(dataArr.filter(v => v.chargeType !== 0)));
  const otherData = summarizeIsContinued(dataArr.filter(v => v.chargeType === 0));
  const dataTotal = data.shift();
  if (!dataTotal) {
    return otherData.filter(Boolean);
  }
  let total: any;
  if (otherData.length > 0) {
    total = otherData.shift();
    if (total) {
      sumPdfData(total, dataTotal);
    } else {
      total = dataTotal;
    }
  } else {
    total = dataTotal;
  }
  data.push(dataTotal);
  data.unshift(total);
  return data.concat(otherData).filter(Boolean);
}

export function createSettlesForPdf(settleYear: number, settleMonth: number, dataList: any[]): [any[][], any[][], any[][]] {
  let dataArr = dataList.reduce((prev: any[], current: any) => {
    if (current.count === 0) return prev;
    const issueYear = Number(current.orgIssueMonth.split('-')[0]);
    const issueMonth = Number(current.orgIssueMonth.split('-')[1]);
    const isContinued = current.method === 1
      ? issueYear !== settleYear || settleMonth !== issueMonth
      : (issueMonth === 12 ? issueYear + 1 : issueYear) !== settleYear || (issueMonth === 12 ? 1 : issueMonth + 1) !== settleMonth;
    let data: any;
    if (current.chargeType === 0) {
      data = initSettlesData();
      data.isContinued = isContinued;
      data.plansName = null;
      data.chargeType = current.chargeType;
      data.chargeTypeName = current.name;
      data.unitPrice = current.count ? current.taxIncAmount / current.count : 0;
      prev.push(data);
    } else {
      const unitPrice = current.count > 0 ? current.taxIncAmount / current.count : 0;
      data = prev.find(v =>
        v.isContinued === isContinued &&
        v.plansName === (current.studioPlanType ? current.studioPlanType.name : null) &&
        v.chargeTypeName === getChargeTypeName(current.chargeType) &&
        v.unitPrice === unitPrice
      );
    }
    if (!data) {
      data = initSettlesData();
      data.isContinued = isContinued;
      data.plansName = current.studioPlanType ? current.studioPlanType.name : null;
      data.chargeType = current.chargeType;
      data.chargeTypeName = getChargeTypeName(current.chargeType);
      data.unitPrice = current.count ? current.taxIncAmount / current.count : 0;
      prev.push(data);
    }
    data.totalCount += current.count;
    data.totalTaxIncAmount += current.taxIncAmount;
    if (current.method === 1) {
      data.btTotalCount += current.count; data.btTotalTaxIncAmount += current.taxIncAmount;
      if ([11, 12, 14, 15].includes(current.subtype)) { data.btTradeCount += current.count; data.btTradeTaxIncAmount += current.taxIncAmount; }
      else if (current.subtype === 13) { data.btErrorCount += current.count; data.btErrorTaxIncAmount += current.taxIncAmount; }
    } else if (current.method === 2) {
      data.ccTotalCount += current.count; data.ccTotalTaxIncAmount += current.taxIncAmount;
      if ([11, 12, 14, 15].includes(current.subtype)) { data.ccTradeCount += current.count; data.ccTradeTaxIncAmount += current.taxIncAmount; }
      else if (current.subtype === 13) { data.ccErrorCount += current.count; data.ccErrorTaxIncAmount += current.taxIncAmount; }
    }
    return prev;
  }, []);
  const sortArr = pdfArraySort(dataArr.filter(v => v.chargeType !== 0));
  const sortArrOther = pdfArraySort(dataArr.filter(v => v.chargeType === 0));
  dataArr = sortArr.concat(sortArrOther);
  if (dataArr.length === 0) return [[], [], []];
  return [
    arrayFillAndSlice(createTotalData(dataArr), 48),
    arrayFillAndSlice(dataArr.filter(v => !v.isContinued), 50),
    arrayFillAndSlice(dataArr.filter(v => v.isContinued), 50),
  ];
}

@Component({
    selector: 'app-balance-list',
    imports: [FormsModule, DatePipe, DecimalPipe, CurrencyPipe, NumericOnlyDirective],
    template: `
<div class="animated fadeIn container-fluid">
  <div class="card">
    <div class="card-header">
      <span><i class="fa s-icon-list"></i> 会費精算書</span>
    </div>
    <div class="card-body">
      <strong>店舗選択</strong>
      @if (balanceDetailDownloadError) { <div class="alert alert-danger" role="alert">{{ balanceDetailDownloadError }}</div> }
      <hr />
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label class="col-form-label" for="studio_name">店舗</label>
            <select class="form-control" id="studio_name" name="studio_name"
              [(ngModel)]="selectedStudio" (ngModelChange)="onStudioChange()">
              <option [ngValue]="null">-- 選択してください --</option>
              @for (studio of studios; track studio.id) {
                <option [ngValue]="studio">{{ studio.name }}</option>
              }
            </select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group row">
            <div class="col-md-12">
              <label class="col-form-label" for="year_pay">締年月</label>
              <div class="from-to-wrap">
                <div class="from-to-input">
                  <select class="form-control" id="year_pay" name="year_pay"
                    [(ngModel)]="selectedYear" (ngModelChange)="loadBalance()">
                    @for (opt of yearOptions; track opt.id) {
                      <option [ngValue]="opt.id">{{ opt.name }}</option>
                    }
                  </select>
                </div>
                <div><span>&nbsp;</span></div>
                <div class="from-to-input">
                  <select class="form-control" id="month_pay" name="month_pay"
                    [(ngModel)]="selectedMonth" (ngModelChange)="loadBalance()">
                    @for (opt of monthOptions; track opt.id) {
                      <option [ngValue]="opt.id">{{ opt.name }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    @if (selectedStudio) {
      @if (!showBalance) {
        <div class="card-body">
          <span>該当するデータはありません</span>
        </div>
      }

      @if (showSettlement) {
        <div class="card-body">
          <div class="form-group row">
            <div class="col-md-12">
              @if (settlement?.status === 1) {
                <button class="btn btn-success" type="button"
                  (click)="openShowBalanceModal()"
                  [disabled]="isPreparingBalanceModal || showBalanceModal"
                  title="会費精算書ダウンロード">
                  <i class="fa s-icon-dawnload"></i>&nbsp;会費精算書ダウンロード
                </button>
                <button type="button" class="btn btn-success"
                  (click)="getDetail()"
                  [disabled]="disableBalanceDetailDownload || showBalanceDetailModal"
                  title="会費精算書内訳明細ダウンロード">
                  <i class="fa s-icon-dawnload"></i>&nbsp;会費精算書内訳明細ダウンロード
                </button>
              }
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <span>
                @if (userPermission <= 10) {
                  <button type="button"
                    [class]="settlement?.status === 0 ? 'text-left pt-0 pb-0 rounded-0 btn btn-block d-flex align-items-center btn-danger' : 'text-left pt-0 pb-0 rounded-0 btn btn-block d-flex align-items-center btn-success'"
                    (click)="openUpdateSettlementModal()">
                    <i [class]="settlement?.status === 0 ? 'fa fa-3x fa-toggle-off' : 'fa fa-3x fa-toggle-on'"></i>
                    @if (settlement?.status === 0) {
                      <span style="vertical-align: super;">&nbsp;編集中(公開日に関わらず非公開状態)</span>
                    }
                    @if (settlement?.status === 1) {
                      <span style="vertical-align: super;">&nbsp;公開中</span>
                    }
                  </button>
                }
              </span>
            </div>
          </div>
        </div>
      }

      @if (showBalance) {
        <div id="settlementBalanceDetail">
          <div id="balanceParts1" class="card-body">
            <div class="col-md-12">
              <div>
                <div class="row">
                  <div class="col-12"><span class="pull-right">発行日　{{ now | date:'yyyy年MM月dd日' }}</span></div>
                  <div class="col-12 mb-2"><h5><b>{{ currentStudio?.owner?.name }} 御中</b></h5></div>
                  <div class="d-flex w-100 mb-4">
                    <div class="col-7">
                      <div><h5 class="mb-0">スマートフィット100 {{ currentStudio?.name }} 会費精算報告書</h5></div>
                      <div><small>{{ selectedYear }}年{{ selectedMonth }}月末締め決済売上分につきまして以下の通りのお支払いをいたします。</small></div>
                      <div><small>　　対象：{{ selectedMonth }}月1日クレジット決済・{{ selectedMonth }}月28日口座振替</small></div>
                    </div>
                    <div class="col-5">
                      <div class="text-right"><small>〒103-0027<br />東京都中央区日本橋蛎殻町2-8-12 岸浪ビル3階</small></div>
                      <div class="text-right"><span>株式会社スマートフィットネス</span></div>
                      <div class="text-right"><span>登録番号 T6010901026005</span></div>
                    </div>
                  </div>
                  <div class="col-3"><h5 class="mb-0"><b>金額: </b></h5></div>
                  <div class="col-9"><h5 class="mb-0"><b>{{ totalPaymentTaxincAmount | number:'1.0-0' }} 円</b></h5></div>
                  <div class="col-3"><small>お支払い日</small></div>
                  <div class="col-9">
                    <small>{{ settlement?.payDate }}</small>
                    @if (userPermission <= 10 && settlement?.status === 0) {
                      <button type="button" class="btn btn-primary text-white btn-sm ml-2"
                        (click)="openEditPayDateModal()" title="編集">
                        <i class="fa s-icon-edit fa-fw"></i>
                      </button>
                    }
                  </div>
                  <hr />
                </div>
              </div>
              <div>
                <div class="row align-items-end">
                  <table class="table table-sm table-responsive-sm">
                    <!-- Income Balance -->
                    <thead class="border-income-balance-thead">
                    <tr class="table-secondary">
                      <th width="16%">入金内訳</th>
                      <th width="15%">&nbsp;</th>
                      <th width="16%">&nbsp;</th>
                      <th width="8%"><span class="pull-right">件数</span></th>
                      @if (userPermission <= 10) {
                        <th width="15%"><span class="pull-right">金額</span></th>
                        <th width="15%"><span class="pull-right">うち消費税</span></th>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) {
                        <th width="20%"></th>
                      }
                      @if (userPermission > 10) {
                        <th width="22%"><span class="pull-right">合計</span></th>
                        <th width="23%"><span class="pull-right">うち消費税</span></th>
                      }
                    </tr>
                    </thead>

                    <!-- Bank Transfer -->
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance">
                      <td>口座振替</td>
                      <td colspan="2">請求金額</td>
                      <td><span class="pull-right">{{ totalCount11 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount11 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount11 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(A) 請求済　入金金額</td>
                      <td><span class="pull-right">{{ subtotalPaymentCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfPaymentAndCollectedUnpaid; track $index) {
                      @if (balance.method === 1) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit fa-fw"></i>
                                </button>
                              </span>
                            </td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金回収金額　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCollectedUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金額</td>
                      <td><span class="pull-right">{{ subtotalUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfUnpaid; track $index) {
                      @if (balance.method === 1) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit fa-fw"></i>
                                </button>
                              </span>
                            </td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashPaymentCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　未入金現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashCollectedUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    </tbody>

                    <!-- Credit Card -->
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance">
                      <td>クレジットカード</td>
                      <td colspan="2">請求金額</td>
                      <td><span class="pull-right">{{ totalCount12 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount12 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount12 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td class="border-0"></td>
                      <td colspan="2" class="border-bottom">(B) 請求済　入金金額</td>
                      <td><span class="pull-right">{{ subtotalPaymentCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfPaymentAndCollectedUnpaid; track $index) {
                      @if (balance.method === 2) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit fa-fw"></i>
                                </button>
                              </span>
                            </td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金回収金額　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCollectedUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td class="border-0"></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金額</td>
                      <td><span class="pull-right">{{ subtotalUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfUnpaid; track $index) {
                      @if (balance.method === 2) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit fa-fw"></i>
                                </button>
                              </span>
                            </td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashPaymentCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　未入金現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashCollectedUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission <= 10 && settlement?.status === 0) { <td></td> }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    </tbody>

                    <!-- Others (type=1, method != 1 && method != 2) -->
                    <tbody class="border-income-balance-tbody">
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 1 && balance.method !== 1 && balance.method !== 2) {
                        <tr class="border-income-balance others">
                          <td>その他</td>
                          <td colspan="2">(H) {{ balance.name }}</td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit fa-fw"></i>
                                </button>
                                @if (balance.status === 0) {
                                  <button type="button" class="btn btn-danger btn-sm"
                                    (click)="openDeleteBalanceModal(balance)" title="削除">
                                    <i class="fa s-icon-delete fa-fw"></i>
                                  </button>
                                }
                              </span>
                            </td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    @if (userPermission <= 10 && settlement?.status === 0) {
                      <tr class="border-income-balance others">
                        <td colspan="7" class="border-right-0">
                          <span class="pull-right">
                            <button type="button" class="btn btn-primary text-white btn-sm"
                              (click)="addSingleBalance(1, true); openUpsertBalanceModal()" title="追加">
                              <i class="fa fa s-icon-plus"></i>&nbsp;入金追加
                            </button>
                            <button type="button" class="btn btn-primary text-white btn-sm"
                              (click)="addSingleBalance(2, true); openUpsertBalanceModal()" title="追加">
                              <i class="fa fa s-icon-plus"></i>&nbsp;未入金追加
                            </button>
                          </span>
                        </td>
                      </tr>
                    }
                    </tbody>

                    <!-- Income total (C) -->
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance bg-gray-org" data-balance-summary="income-10">
                      <th>入金計（A + B@if (incomeBalanceForOthersExists) { + H }）</th><th colspan="3">10％対象</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1Per10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1Per10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr><tr class="border-income-balance bg-gray-org" data-balance-summary="income-8">
                      <th></th><th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1Per8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1Per8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr><tr class="border-income-balance border-bottom bg-gray-org" data-balance-summary="income-total">
                      <th colspan="4">(C) 入金総計</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr>
                    <tr class="border-income-balance">
                      <td colspan="6" class="border-right-0">&nbsp;</td>
                      @if (userPermission <= 10 && settlement?.status === 0) { <td>&nbsp;</td> }
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div id="balanceParts2" class="card-body">
            <div class="col-md-12">
              <div>
                <div class="row align-items-end">
                  <table class="table table-sm table-responsive-sm">
                    <!-- Expense Balance -->
                    <thead class="border-income-balance-thead">
                    <tr class="table-secondary">
                      <th colspan="6">費用内訳</th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>
                    </thead>

                    <!-- Type 3: taxed expenses -->
                    <tbody class="border-income-balance-tbody">
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 3) {
                        <tr class="border-expense-balance">
                          <td>
                            @if (balance.method === 0 && balance.subtype === 30) { <span>その他費用</span> }
                            @if (balance.method === 1) { <span>口座振替</span> }
                            @if (balance.method === 2) { <span>クレジットカード</span> }
                          </td>
                          @if (balance.subtype !== 35 && balance.subtype !== 36 && balance.subtype !== 37) {
                            <td colspan="3">{{ balance.name }}</td>
                          }
                          @if (balance.subtype === 35 || balance.subtype === 36 || balance.subtype === 37) {
                            <td>{{ balance.name }}</td>
                            <td><span class="pull-right">¥{{ balance.unitValue | number:'1.0-0' }} /件</span></td>
                            <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          }
                          <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit"></i>
                                </button>
                              </span>
                            </td>
                          }
                        </tr>
                      }
                    }
                    @if (userPermission <= 10 && settlement?.status === 0) {
                      <tr class="border-expense-balance">
                        <td colspan="7">
                          <span class="pull-right">
                            <button type="button" class="btn btn-primary text-white btn-sm"
                              (click)="addSingleBalance(3, true); openUpsertBalanceModal()" title="追加">
                              <i class="fa fa s-icon-plus"></i>&nbsp;課税費用追加
                            </button>
                          </span>
                        </td>
                      </tr>
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th>(D) 課税費用計</th>
                      <th colspan="3">10％対象</th>
                      <th><span class="pull-right">¥{{ totalTaxincAmount3Per10 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount3Per10 | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th class="border-top-0"></th>
                      <th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">¥{{ totalTaxincAmount3Per8 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount3Per8 | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>

                    <!-- Type 4: non-taxed expenses -->
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 4) {
                        <tr class="border-expense-balance">
                          <td width="10%">
                            @if (balance.method === 0 && balance.subtype === 40) { <span>その他費用</span> }
                            @if (balance.method === 1) { <span>口座振替</span> }
                            @if (balance.method === 2) { <span>クレジットカード</span> }
                          </td>
                          <td>{{ balance.name }} (非課税)</td>
                          <td>
                            @if (balance.subtype === 41) {
                              <span class="pull-right">{{ balance.unitValue }} %</span>
                            }
                          </td>
                          <td>
                            @if (balance.subtype !== 41) {
                              <span class="pull-right">{{ balance.count }} 件</span>
                            }
                          </td>
                          <td><span class="pull-right">¥{{ balance.taxExcAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, false); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit"></i>
                                </button>
                              </span>
                            </td>
                          }
                        </tr>
                      }
                    }
                    @if (userPermission <= 10 && settlement?.status === 0) {
                      <tr class="border-expense-balance">
                        <td colspan="7">
                          <span class="pull-right">
                            <button type="button" class="btn btn-primary text-white btn-sm"
                              (click)="addSingleBalance(4, false); openUpsertBalanceModal()" title="追加">
                              <i class="fa fa s-icon-plus"></i>&nbsp;非課税費用追加
                            </button>
                          </span>
                        </td>
                      </tr>
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th colspan="4">(E) 非課税費用計</th>
                      <th><span class="pull-right">¥{{ totalTaxexcAmount4 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount4 | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>

                    <!-- Type 5: advances (subtype=50) -->
                    @for (balance of balances; track balance.id) {
                      @if (balance.subtype === 50) {
                        <tr class="border-expense-balance">
                          <td><span>その他費用</span></td>
                          <td>{{ balance.name }}</td>
                          <td colspan="2">【{{ balance.advancePayeeName }}】{{ balance.isInvoiceRegistrar ? '〇' : '' }}</td>
                          <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                          @if (userPermission <= 10 && settlement?.status === 0) {
                            <td>
                              <span class="pull-right">
                                <button type="button" class="btn btn-primary text-white btn-sm"
                                  (click)="editSingleBalance(balance, true); openUpsertBalanceModal()" title="編集">
                                  <i class="fa s-icon-edit"></i>
                                </button>
                              </span>
                            </td>
                          }
                        </tr>
                      }
                    }
                    @if (userPermission <= 10 && settlement?.status === 0) {
                      <tr class="border-expense-balance">
                        <td colspan="7">
                          <span class="pull-right">
                            <button type="button" class="btn btn-primary text-white btn-sm"
                              (click)="addSingleBalance(5, true); openUpsertBalanceModal()" title="追加">
                              <i class="fa fa s-icon-plus"></i>&nbsp;立替金追加
                            </button>
                          </span>
                        </td>
                      </tr>
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th>(F) 立替金</th>
                      <th colspan="3">10％対象</th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxincAmountPar10 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxAmountPar10 | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th class="border-top-0"><small style="font-size: 10px; vertical-align: top;">〇:インボイス登録事業者</small></th>
                      <th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxincAmountPar8 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxAmountPar8 | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th colspan="4">(G) 費用計 (D + E + F)</th>
                      <th><span class="pull-right">¥{{ totalExpenseTaxincAmount | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalExpenseTaxAmount | number:'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement?.status === 0) { <th>&nbsp;</th> }
                    </tr>
                    <tr class="border-expense-balance">
                      <td colspan="6">&nbsp;</td>
                      @if (userPermission <= 10 && settlement?.status === 0) { <td>&nbsp;</td> }
                    </tr>
                    <tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-10">
                      <th>お支払金額</th><th colspan="3">10％対象</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmountPer10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmountPer10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr><tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-8">
                      <th></th><th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmountPer8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmountPer8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr><tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-exempt">
                      <th colspan="4">非課税費用（控除）</th>
                      <th><span class="pull-right">{{ -totalTaxexcAmount4 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ -totalTaxAmount4 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr>@if (paymentZeroAdjustmentAmount || paymentZeroAdjustmentTaxAmount) { <tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-adjustment">
                      <th colspan="4">お支払総計の0円調整</th>
                      <th><span class="pull-right">{{ paymentZeroAdjustmentAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ paymentZeroAdjustmentTaxAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr> }<tr class="table-primary border-expense-balance border-bottom" data-balance-summary="payment-total">
                      <th colspan="4">(H) お支払総計 (C - G - Z)</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      @if (userPermission <= 10 && settlement.status == 0) { <th>&nbsp;</th> }
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    }
  </div>
</div>

<!-- Modal: updateSettlementModal -->
@if (showUpdateSettlementModal) {
  <div class="modal-backdrop fade show" (click)="closeUpdateSettlementModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">会費精算書の公開制御</h5>
          <button type="button" class="close" (click)="closeUpdateSettlementModal()" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">公開日</label>
            <div class="col-md-12">
              <input id="settlement_published_at" name="settlement_published_at"
                [(ngModel)]="settlementPublishedAt" type="date" class="form-control">
            </div>
            <label class="col-md-12 col-form-label">ステータス</label>
            <div class="col-md-12">
              <select class="form-control" [(ngModel)]="settlementStatus">
                <option value="0">編集中(公開日に関わらず非公開状態)</option>
                <option value="1">公開中</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeUpdateSettlementModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button type="button" class="btn btn-success" [disabled]="!settlementPublishedAt"
            (click)="submitSettlementUpdate()">
            <i class="fa s-icon-save"></i>&nbsp;保存
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: upsertBalanceModal -->
@if (showUpsertBalanceModal) {
  <div class="modal-backdrop fade show" (click)="closeUpsertBalanceModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">精算書データの追加・編集</h5>
          <button type="button" class="close" (click)="closeUpsertBalanceModal()" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group row">
            @if (isEdit) {
              <div class="col-md-12">
                <label class="col-form-label" for="edit_target">編集データ</label>
                <select class="form-control" id="edit_target" [(ngModel)]="singleBalance">
                  <option [ngValue]="undefined">-- 選択してください --</option>
                  @for (sb of singleBalances; track sb.id) {
                    <option [ngValue]="sb">{{ sb.selectName }}</option>
                  }
                </select>
              </div>
            }
            <label class="col-md-12 col-form-label">タイトル</label>
            <div class="col-md-12">
              <input type="text" class="form-control col-md-12"
                [ngModel]="singleBalance?.name" (ngModelChange)="singleBalance.name = $event"
                [disabled]="isSingleBalanceInputDisabled()">
            </div>
            @if (isAddIncome) {
              <div class="col-md-12">
                <label class="col-form-label">売上タイプ</label>
                <select class="form-control" [(ngModel)]="singleBalance.chargeType">
                  <option [ngValue]="undefined">-- 選択してください --</option>
                  @for (opt of chargeTypeOptions; track opt.id) {
                    <option [ngValue]="opt.id">{{ opt.name }}</option>
                  }
                </select>
              </div>
            }
            @if (singleBalance?.chargeType === 1) {
              <div class="col-md-12">
                <label class="col-form-label">コースタイプ</label>
                <select class="form-control" [(ngModel)]="singleBalance.studioPlanTypeId">
                  <option [ngValue]="undefined">-- 選択してください --</option>
                  @for (opt of studioPlanTypeOptions; track opt.id) {
                    <option [ngValue]="opt.id">{{ opt.name }}</option>
                  }
                </select>
              </div>
            }
            @if (isIncome) {
              <div class="col-md-12">
                <label class="col-form-label">支払方法</label>
                <select class="form-control" [(ngModel)]="singleBalance.method">
                  <option [ngValue]="undefined">-- 選択してください --</option>
                  @for (opt of methodOptions; track opt.id) {
                    <option [ngValue]="opt.id">{{ opt.name }}</option>
                  }
                </select>
              </div>
            }
            @if (singleBalance?.subtype !== 50) {
              <label class="col-md-12 col-form-label">件数</label>
              <div class="col-md-12">
                <input type="text" class="form-control col-md-12"
                  [ngModel]="singleBalance?.count" (ngModelChange)="singleBalance.count = $event" sfNumericOnly inputmode="numeric"
                  [disabled]="isSingleBalanceInputDisabled()">
              </div>
            }
            @if (isIncludeTax) {
              <label class="col-md-12 col-form-label">金額(税込)</label>
              <div class="col-md-12">
                <input type="number" class="form-control col-md-12"
                  [ngModel]="singleBalance?.taxIncAmount" (ngModelChange)="singleBalance.taxIncAmount = $event"
                  [disabled]="isSingleBalanceInputDisabled()">
              </div>
            }
            @if (singleBalance?.type === 1 || singleBalance?.type === 3 || singleBalance?.subtype === 50) {
              <div class="col-md-12">
                <label class="col-form-label">消費税率</label>
                <select class="form-control" [(ngModel)]="singleBalance.taxRateId">
                  <option [ngValue]="undefined">-- 選択してください --</option>
                  @for (rate of taxRates; track rate.id) {
                    <option [ngValue]="rate.id">{{ rate.name }}</option>
                  }
                </select>
              </div>
            }
            @if (!isIncludeTax) {
              <label class="col-md-12 col-form-label">金額(税抜き)</label>
              <div class="col-md-12">
                <input type="number" class="form-control col-md-12"
                  [ngModel]="singleBalance?.taxExcAmount" (ngModelChange)="singleBalance.taxExcAmount = $event"
                  [disabled]="isSingleBalanceInputDisabled()">
              </div>
            }
            @if (singleBalance?.subtype === 50) {
              <label class="col-md-12 col-form-label">立替取引先名</label>
              <div class="col-md-12">
                <input type="text" class="form-control col-md-12"
                  [ngModel]="singleBalance?.advancePayeeName" (ngModelChange)="singleBalance.advancePayeeName = $event"
                  [disabled]="isSingleBalanceInputDisabled()">
              </div>
              <div class="col-md-12 pt-1">
                <div class="custom-control custom-checkbox">
                  <input type="checkbox" class="custom-control-input"
                    [ngModel]="singleBalance?.isInvoiceRegistrar === 1 || singleBalance?.isInvoiceRegistrar === true"
                    (ngModelChange)="singleBalance.isInvoiceRegistrar = $event ? 1 : 0"
                    [disabled]="isSingleBalanceInputDisabled()"
                    id="is_invoice_registrar" name="is_invoice_registrar">
                  <label class="custom-control-label" for="is_invoice_registrar">インボイス登録事業者</label>
                </div>
              </div>
            }
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeUpsertBalanceModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" [disabled]="isSingleBalanceInvalid()"
            (click)="saveSingleBalance(singleBalance, isIncludeTax); closeUpsertBalanceModal()">
            <i class="fa s-icon-save"></i>&nbsp;保存
          </button>
          @if (singleBalance?.id && singleBalance?.status === 0) {
            <button type="button" class="btn btn-danger"
              (click)="closeUpsertBalanceModal(); openDeleteBalanceModal(singleBalance)" title="削除">
              <i class="fa s-icon-delete"></i>&nbsp;削除
            </button>
          }
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: updateBalanceStatusModal (delete confirmation) -->
@if (showUpdateBalanceStatusModal) {
  <div class="modal-backdrop fade show" (click)="closeUpdateBalanceStatusModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title">精算書データの削除</h5>
          <button type="button" class="close" (click)="closeUpdateBalanceStatusModal()" aria-label="Close">
            <span class="text-white" aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">本当に削除しますか？</label>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="closeUpdateBalanceStatusModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-danger" type="button"
            (click)="removeBalance(singleBalance); closeUpdateBalanceStatusModal()">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: showBalanceDetailModal -->
@if (showBalanceDetailModal) {
  <div class="modal-backdrop fade show" (click)="closeBalanceDetailModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">会費精算書内訳明細のダウンロード</h5>
          <button type="button" class="close" (click)="closeBalanceDetailModal()" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">
              @if (disableBalanceDetailDownload) {
                <i class="fa fa-spinner fa-spin"></i>&nbsp;内訳データを確認しています。
              } @else if (balanceDetailLoadError) {
                {{ balanceDetailLoadError }}
              } @else if (!hasBalanceDetailData) {
                対象期間の内訳データはありません。データなしの明細をダウンロードできます。
              } @else {
                会費精算書内訳明細をダウンロードしますか？
              }
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeBalanceDetailModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button"
            (click)="downloadBalanceDetailAsPdf()"
            [disabled]="disableBalanceDetailDownload || isDownloadingBalanceDetailPdf || !!balanceDetailLoadError">
            <i class="fa s-icon-dawnload"></i>&nbsp;{{ isDownloadingBalanceDetailPdf ? '作成中...' : 'ダウンロード' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: preparing balance preview -->
@if (isPreparingBalanceModal) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-busy="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">会費精算書のダウンロード</h5>
        </div>
        <div class="modal-body text-center py-5">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <div class="mt-3">プレビューを準備しています。</div>
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: showBalanceModal (display report inline) -->
@if (showBalanceModal) {
  <div class="modal-backdrop fade show" (click)="closeShowBalanceModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document" style="max-width: 790px !important; min-width: 790px !important;">
      <div class="modal-content">
        <div id="balanceDetail">
          <div id="settlementBalanceDetail">
            <div class="card-body">
            <div class="col-md-12">
              <div>
                <div class="row">
                  <div class="col-12"><span class="pull-right">発行日　{{ now | date:'yyyy年MM月dd日' }}</span></div>
                  <div class="col-12 mb-2"><h5><b>{{ currentStudio?.owner?.name }} 御中</b></h5></div>
                  <div class="d-flex w-100 mb-4">
                    <div class="col-7">
                      <div><h5 class="mb-0">スマートフィット100 {{ currentStudio?.name }} 会費精算報告書</h5></div>
                      <div><small>{{ selectedYear }}年{{ selectedMonth }}月末締め決済売上分につきまして以下の通りのお支払いをいたします。</small></div>
                      <div><small>　　対象：{{ selectedMonth }}月1日クレジット決済・{{ selectedMonth }}月28日口座振替</small></div>
                    </div>
                    <div class="col-5">
                      <div class="text-right"><small>〒103-0027<br />東京都中央区日本橋蛎殻町2-8-12 岸浪ビル3階</small></div>
                      <div class="text-right"><span>株式会社スマートフィットネス</span></div>
                      <div class="text-right"><span>登録番号 T6010901026005</span></div>
                    </div>
                  </div>
                  <div class="col-3"><h5 class="mb-0"><b>金額: </b></h5></div>
                  <div class="col-9"><h5 class="mb-0"><b>{{ totalPaymentTaxincAmount | number:'1.0-0' }} 円</b></h5></div>
                  <div class="col-3"><small>お支払い日</small></div>
                  <div class="col-9"><small>{{ settlement?.payDate }}</small></div>
                  <hr />
                </div>
              </div>
              <div>
                <div class="row align-items-end">
                  <table class="table table-sm table-responsive-sm">
                    <thead class="border-income-balance-thead">
                    <tr class="table-secondary">
                      <th width="16%">入金内訳</th>
                      <th width="15%">&nbsp;</th>
                      <th width="16%">&nbsp;</th>
                      <th width="8%"><span class="pull-right">件数</span></th>
                      @if (userPermission <= 10) {
                        <th width="15%"><span class="pull-right">金額</span></th>
                        <th width="15%"><span class="pull-right">うち消費税</span></th>
                      }
                      @if (userPermission > 10) {
                        <th width="22%"><span class="pull-right">合計</span></th>
                        <th width="23%"><span class="pull-right">うち消費税</span></th>
                      }
                    </tr>
                    </thead>
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance">
                      <td>口座振替</td>
                      <td colspan="2">請求金額</td>
                      <td><span class="pull-right">{{ totalCount11 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount11 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount11 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(A) 請求済　入金金額</td>
                      <td><span class="pull-right">{{ subtotalPaymentCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfPaymentAndCollectedUnpaid; track $index) {
                      @if (balance.method === 1) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金回収金額　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCollectedUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金額</td>
                      <td><span class="pull-right">{{ subtotalUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfUnpaid; track $index) {
                      @if (balance.method === 1) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashPaymentCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　未入金現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashCollectedUnpaidCount1 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount1 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    </tbody>
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance">
                      <td>クレジットカード</td>
                      <td colspan="2">請求金額</td>
                      <td><span class="pull-right">{{ totalCount12 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount12 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ totalTaxincAmount12 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td class="border-0"></td>
                      <td colspan="2" class="border-bottom">(B) 請求済　入金金額</td>
                      <td><span class="pull-right">{{ subtotalPaymentCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfPaymentAndCollectedUnpaid; track $index) {
                      @if (balance.method === 2) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金回収金額　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCollectedUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td class="border-0"></td>
                      <td colspan="2" class="border-bottom">　請求済　未入金額</td>
                      <td><span class="pull-right">{{ subtotalUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    @for (balance of aggregateDataOfUnpaid; track $index) {
                      @if (balance.method === 2) {
                        <tr class="border-income-balance">
                          <td></td>
                          <td class="border-top-0"></td>
                          <td>
                            @if (balance.chargeType === 1) { <span>月会費</span> }
                            @if (balance.chargeType === 2) { <span>入会金・事務手数料</span> }
                            @if (balance.chargeType === 3) { <span>契約ロッカー</span> }
                            @if (balance.chargeType === 4) { <span>パーソナルトレーニング</span> }
                            @if (balance.chargeType === 5) { <span>その他①</span> }
                            @if (balance.chargeType === 0) { <span>その他②</span> }
                          </td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashPaymentCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashPaymentAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    <tr class="border-income-balance">
                      <td></td>
                      <td colspan="2" class="border-bottom">(Z) 請求済　未入金現金回収　※「入金金額」内訳に含む</td>
                      <td><span class="pull-right">{{ subtotalCashCollectedUnpaidCount2 }} 件</span></td>
                      @if (userPermission <= 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                      @if (userPermission > 10) {
                        <td><span class="pull-right">¥{{ subtotalCashCollectedUnpaidAmount2 | number:'1.0-0' }}</span></td>
                        <td><span class="pull-right"></span></td>
                      }
                    </tr>
                    </tbody>
                    <tbody class="border-income-balance-tbody">
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 1 && balance.method !== 1 && balance.method !== 2) {
                        <tr class="border-income-balance others">
                          <td>その他</td>
                          <td colspan="2">(H) {{ balance.name }}</td>
                          <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          @if (userPermission <= 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                          @if (userPermission > 10) {
                            <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                            <td><span class="pull-right"></span></td>
                          }
                        </tr>
                      }
                    }
                    </tbody>
                    <tbody class="border-income-balance-tbody">
                    <tr class="border-income-balance bg-gray-org" data-balance-summary="income-10">
                      <th>入金計（A + B@if (incomeBalanceForOthersExists) { + H }）</th><th colspan="3">10％対象</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1Per10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1Per10 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr><tr class="border-income-balance bg-gray-org" data-balance-summary="income-8">
                      <th></th><th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1Per8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1Per8 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr><tr class="border-income-balance border-bottom bg-gray-org" data-balance-summary="income-total">
                      <th colspan="4">(C) 入金総計</th>
                      <th><span class="pull-right">{{ totalTaxincAmount1 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalTaxAmount1 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr>
                    <tr class="border-income-balance">
                      <td colspan="6" class="border-right-0">&nbsp;</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="col-md-12">
              <div>
                <div class="row align-items-end">
                  <table class="table table-sm table-responsive-sm">
                    <thead class="border-income-balance-thead">
                    <tr class="table-secondary">
                      <th colspan="6">費用内訳</th>
                    </tr>
                    </thead>
                    <tbody class="border-income-balance-tbody">
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 3) {
                        <tr class="border-expense-balance">
                          <td>
                            @if (balance.method === 0 && balance.subtype === 30) { <span>その他費用</span> }
                            @if (balance.method === 1) { <span>口座振替</span> }
                            @if (balance.method === 2) { <span>クレジットカード</span> }
                          </td>
                          @if (balance.subtype !== 35 && balance.subtype !== 36 && balance.subtype !== 37) {
                            <td colspan="3">{{ balance.name }}</td>
                          }
                          @if (balance.subtype === 35 || balance.subtype === 36 || balance.subtype === 37) {
                            <td>{{ balance.name }}</td>
                            <td><span class="pull-right">¥{{ balance.unitValue | number:'1.0-0' }} /件</span></td>
                            <td><span class="pull-right">{{ balance.count }} 件</span></td>
                          }
                          <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                        </tr>
                      }
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th>(D) 課税費用計</th>
                      <th colspan="3">10％対象</th>
                      <th><span class="pull-right">¥{{ totalTaxincAmount3Per10 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount3Per10 | number:'1.0-0' }}</span></th>
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th class="border-top-0"></th>
                      <th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">¥{{ totalTaxincAmount3Per8 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount3Per8 | number:'1.0-0' }}</span></th>
                    </tr>
                    @for (balance of balances; track balance.id) {
                      @if (balance.type === 4) {
                        <tr class="border-expense-balance">
                          <td width="10%">
                            @if (balance.method === 0 && balance.subtype === 40) { <span>その他費用</span> }
                            @if (balance.method === 1) { <span>口座振替</span> }
                            @if (balance.method === 2) { <span>クレジットカード</span> }
                          </td>
                          <td>{{ balance.name }} (非課税)</td>
                          <td>
                            @if (balance.subtype === 41) {
                              <span class="pull-right">{{ balance.unitValue }} %</span>
                            }
                          </td>
                          <td>
                            @if (balance.subtype !== 41) {
                              <span class="pull-right">{{ balance.count }} 件</span>
                            }
                          </td>
                          <td><span class="pull-right">¥{{ balance.taxExcAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                        </tr>
                      }
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th colspan="4">(E) 非課税費用計</th>
                      <th><span class="pull-right">¥{{ totalTaxexcAmount4 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalTaxAmount4 | number:'1.0-0' }}</span></th>
                    </tr>
                    @for (balance of balances; track balance.id) {
                      @if (balance.subtype === 50) {
                        <tr class="border-expense-balance">
                          <td><span>その他費用</span></td>
                          <td>{{ balance.name }}</td>
                          <td colspan="2">【{{ balance.advancePayeeName }}】{{ balance.isInvoiceRegistrar ? '〇' : '' }}</td>
                          <td><span class="pull-right">¥{{ balance.taxIncAmount | number:'1.0-0' }}</span></td>
                          <td><span class="pull-right"></span></td>
                        </tr>
                      }
                    }
                    <tr class="border-expense-balance bg-gray-org">
                      <th>(F) 立替金</th>
                      <th colspan="3">10％対象</th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxincAmountPar10 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxAmountPar10 | number:'1.0-0' }}</span></th>
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th class="border-top-0"><small style="font-size: 10px; vertical-align: top;">〇:インボイス登録事業者</small></th>
                      <th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxincAmountPar8 | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalAdvancesTaxAmountPar8 | number:'1.0-0' }}</span></th>
                    </tr>
                    <tr class="border-expense-balance border-bottom bg-gray-org">
                      <th colspan="4">(G) 費用計 (D + E + F)</th>
                      <th><span class="pull-right">¥{{ totalExpenseTaxincAmount | number:'1.0-0' }}</span></th>
                      <th><span class="pull-right">¥{{ totalExpenseTaxAmount | number:'1.0-0' }}</span></th>
                    </tr>
                    <tr class="border-expense-balance">
                      <td colspan="6">&nbsp;</td>
                    </tr>
                    <tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-10">
                      <th>お支払金額</th><th colspan="3">10％対象</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmountPer10 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmountPer10 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr><tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-8">
                      <th></th><th colspan="3">8％対象 ＊軽減税率対象</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmountPer8 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmountPer8 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr><tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-exempt">
                      <th colspan="4">非課税費用（控除）</th>
                      <th><span class="pull-right">{{ -totalTaxexcAmount4 | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ -totalTaxAmount4 | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr>@if (paymentZeroAdjustmentAmount || paymentZeroAdjustmentTaxAmount) { <tr class="border-expense-balance bg-gray-org" data-balance-summary="payment-adjustment">
                      <th colspan="4">お支払総計の0円調整</th>
                      <th><span class="pull-right">{{ paymentZeroAdjustmentAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ paymentZeroAdjustmentTaxAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr> }<tr class="table-primary border-expense-balance border-bottom" data-balance-summary="payment-total">
                      <th colspan="4">(H) お支払総計 (C - G - Z)</th>
                      <th><span class="pull-right">{{ totalPaymentTaxincAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>
                      <th><span class="pull-right">{{ totalPaymentTaxAmount | currency:'¥':'symbol':'1.0-0' }}</span></th>

                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeShowBalanceModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="downloadBalanceAsPdf()"
            [disabled]="isDownloadingBalancePdf">
            <i class="fa s-icon-dawnload"></i>&nbsp;{{ isDownloadingBalancePdf ? '作成中...' : 'ダウンロード' }}
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Modal: editPayDateModal -->
@if (showEditPayDateModal) {
  <div class="modal-backdrop fade show" (click)="closeEditPayDateModal()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">お支払い日の設定</h5>
          <button type="button" class="close" (click)="closeEditPayDateModal()" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">お支払い日</label>
            <div class="col-md-12">
              <input id="settlement_pay_date" name="settlement_pay_date"
                [(ngModel)]="settlementPayDate" type="date" class="form-control">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="closeEditPayDateModal()">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button type="button" class="btn btn-success" [disabled]="!settlementPayDate"
            (click)="updatePayDate(settlement.settleMonth, settlementPayDate); closeEditPayDateModal()">
            <i class="fa s-icon-save"></i>&nbsp;保存
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Hidden area for 内訳明細 PDF generation -->
<div id="showBalanceDetailWrap" style="display: none;">
  <div id="showBalanceDetail">
    @if (!hasBalanceDetailData) {
      <div style="margin-top: 30px;">
        <div class="d-flex justify-content-between">
          <h5>{{ selectedYear }}年{{ selectedMonth }}月末締め売上分 会費精算書内訳明細 {{ currentStudio?.name }}</h5>
          <p>発行日 {{ now | date:'yyyy年MM月dd日' }}</p>
        </div>
        <p>対象期間: {{ selectedMonth }}月1日クレジット {{ selectedMonth }}月28日口座振替</p>
        <div class="text-center" style="margin-top: 80px; font-size: 16px;">
          対象期間の内訳データはありません。
        </div>
      </div>
    }
    @for (settle of settleForPdfTotal; track $index; let outerLast = $last) {
      <div [style.margin-top]="$first ? '30px' : '50px'">
        @if ($first) {
          <div class="d-flex justify-content-between">
            <h5>{{ selectedYear }}年{{ selectedMonth }}月末締め売上分 会費精算書内訳明細 {{ currentStudio?.name }}</h5>
            <p>発行日 {{ now | date:'yyyy年MM月dd日' }}</p>
          </div>
          <p>対象期間: {{ selectedMonth }}月1日クレジット {{ selectedMonth }}月28日口座振替</p>
        }
        <table>
          <tr>
            <td colspan="5" class="border-0"></td>
            <td colspan="6" class="border-0 text-left">※今月請求　内訳明細（合計）</td>
            <td colspan="12" class="border-0">単位：円</td>
          </tr>
          <tr class="bg-1">
            <th colspan="5" class="border-0"></th>
            <th colspan="6">合計</th><th colspan="6">口座振替</th><th colspan="6">クレジット</th>
          </tr>
          <tr class="bg-2">
            <th colspan="2">商品種別</th><th colspan="2">コースタイプ</th><th>単価</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
          </tr>
          <tbody>
            @for (balanceDetail of settle; track $index; let innerLast = $last) {
              <tr [class.pdf-padding]="balanceDetail.isPadding" [style.background-color]="balanceDetail.isTotal ? '#FFF2CC' : '#FFF'">
                @if (balanceDetail.isPadding) {
                  <td colspan="2">&nbsp;</td><td colspan="2">&nbsp;</td>
                  @for (column of pdfBlankColumns; track $index) { <td>&nbsp;</td> }
                } @else {
                @if ((!outerLast || !innerLast) && !balanceDetail.isTotal) {
                  <td colspan="2" class="text-left">{{ balanceDetail.chargeTypeName }}</td>
                  <td colspan="2" class="text-left">{{ balanceDetail.plansName }}</td>
                  <td>{{ balanceDetail.unitPrice | number:'1.0-0' }}</td>
                }
                @if ((!outerLast || !innerLast) && balanceDetail.isTotal) {
                  <td colspan="5" class="text-center">計</td>
                }
                @if (outerLast && innerLast) {
                  <td colspan="5" class="text-center">合計</td>
                }
                <td>{{ balanceDetail.totalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.totalCount | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeTaxIncAmount + balanceDetail.ccTradeTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeCount + balanceDetail.ccTradeCount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorTaxIncAmount + balanceDetail.ccErrorTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorCount + balanceDetail.ccErrorCount) | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorCount | number:'1.0-0' }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
        @if (!outerLast || settleForPdfThisMonth.length > 0 || settleForPdfContinued.length > 0) {
          <div class="html2pdf__page-break"></div>
        }
      </div>
    }
    @for (settle of settleForPdfThisMonth; track $index; let outerLast = $last) {
      <div class="mt-5">
        <table>
          <tr><td colspan="23" class="border-0">単位：円</td></tr>
          <tr class="bg-0">
            <td colspan="5" class="border-0"></td>
            <td colspan="6" class="border-0 text-left">※今月請求　内訳明細（合計）</td>
            <th colspan="6">{{ thisIssueMonthBt | date:'yyyy年MM月' }}分会費 ※{{ selectedMonth }}月28日　口座振替分</th>
            <th colspan="6">{{ thisIssueMonthCc | date:'yyyy年MM月' }}分会費 ※{{ selectedMonth }}月1日　クレジット決済分</th>
          </tr>
          <tr class="bg-1">
            <th colspan="5" class="border-0"></th>
            <th colspan="6">合計</th><th colspan="6">口座振替</th><th colspan="6">クレジット</th>
          </tr>
          <tr class="bg-2">
            <th colspan="2">商品種別</th><th colspan="2">コースタイプ</th><th>単価</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
          </tr>
          <tbody>
            @for (balanceDetail of settle; track $index) {
              <tr class="bg-white" [class.pdf-padding]="balanceDetail.isPadding">
                @if (balanceDetail.isPadding) {
                  <td colspan="2">&nbsp;</td><td colspan="2">&nbsp;</td>
                  @for (column of pdfBlankColumns; track $index) { <td>&nbsp;</td> }
                } @else {
                <td colspan="2" class="text-left">{{ balanceDetail.chargeTypeName }}</td>
                <td colspan="2" class="text-left">{{ balanceDetail.plansName }}</td>
                <td>{{ balanceDetail.unitPrice | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.totalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.totalCount | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeTaxIncAmount + balanceDetail.ccTradeTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeCount + balanceDetail.ccTradeCount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorTaxIncAmount + balanceDetail.ccErrorTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorCount + balanceDetail.ccErrorCount) | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorCount | number:'1.0-0' }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
        @if (!outerLast || settleForPdfContinued.length > 0) {
          <div class="html2pdf__page-break"></div>
        }
      </div>
    }
    @for (settle of settleForPdfContinued; track $index; let outerLast = $last) {
      <div class="mt-5">
        <table>
          <tr><td colspan="23" class="border-0">単位：円</td></tr>
          <tr class="bg-0">
            <td colspan="5" class="border-0"></td>
            <td colspan="6" class="border-0 text-left">※今月請求のうち前月までの会費　内訳明細</td>
            <th colspan="6">{{ continuedIssueMonthBt | date:'yyyy年MM月' }}まで</th>
            <th colspan="6">{{ continuedIssueMonthCc | date:'yyyy年MM月' }}まで</th>
          </tr>
          <tr class="bg-1">
            <th colspan="5" class="border-0"></th>
            <th colspan="6">合計</th><th colspan="6">口座振替</th><th colspan="6">クレジット</th>
          </tr>
          <tr class="bg-2">
            <th colspan="2">商品種別</th><th colspan="2">コースタイプ</th><th>単価</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
            <th>請求金額</th><th>件数</th><th>入金金額</th><th>件数</th><th>未入金金額</th><th>件数</th>
          </tr>
          <tbody>
            @for (balanceDetail of settle; track $index) {
              <tr class="bg-white" [class.pdf-padding]="balanceDetail.isPadding">
                @if (balanceDetail.isPadding) {
                  <td colspan="2">&nbsp;</td><td colspan="2">&nbsp;</td>
                  @for (column of pdfBlankColumns; track $index) { <td>&nbsp;</td> }
                } @else {
                <td colspan="2" class="text-left">{{ balanceDetail.chargeTypeName }}</td>
                <td colspan="2" class="text-left">{{ balanceDetail.plansName }}</td>
                <td>{{ balanceDetail.unitPrice | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.totalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.totalCount | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeTaxIncAmount + balanceDetail.ccTradeTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btTradeCount + balanceDetail.ccTradeCount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorTaxIncAmount + balanceDetail.ccErrorTaxIncAmount) | number:'1.0-0' }}</td>
                <td>{{ (balanceDetail.btErrorCount + balanceDetail.ccErrorCount) | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.btErrorCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTotalCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccTradeCount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorTaxIncAmount | number:'1.0-0' }}</td>
                <td>{{ balanceDetail.ccErrorCount | number:'1.0-0' }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
        @if (!outerLast) {
          <div class="html2pdf__page-break"></div>
        }
      </div>
    }
  </div>
</div>
  `
})
export class BalanceListComponent implements OnInit {
  readonly pdfBlankColumns = Array.from({ length: 19 });
  private pageTitleService = inject(PageTitleService);
  private studioService = inject(StudioService);
  private balanceService = inject(BalanceService);
  private taxRateService = inject(TaxRateService);
  private authState = inject(AuthStateService);
  private studioSelection = inject(StudioSelectionService);

  // --- Options ---
  readonly yearOptions = buildYearOptions();
  readonly monthOptions = buildMonthOptions();
  readonly methodOptions = [{ id: 1, name: '口座振替' }, { id: 2, name: 'クレジットカード' }];
  readonly chargeTypeOptions = [
    { id: 1, name: '月会費' }, { id: 2, name: '入会金・事務手数料' },
    { id: 3, name: '契約ロッカー' }, { id: 4, name: 'パーソナルトレーニング' },
    { id: 5, name: '物販' }, { id: 0, name: 'その他' },
  ];

  // --- Selection state ---
  studios: any[] = [];
  selectedStudio: any = null;
  selectedYear: number = getDefaultYear();
  selectedMonth: number = getDefaultMonth();

  // --- Reference data ---
  taxRates: any[] = [];
  studioPlanTypeOptions: any[] = [];

  // --- Settlement display flags ---
  showSettlement = false;
  showBalance = false;

  // --- Core data ---
  settlement: any = {};
  balances: any[] = [];
  currentStudio: any = null;
  now: Date = new JstDate();
  incomeBalanceForOthersExists = false;

  // --- type-99 display-only spliced items ---
  collectedUnpaidForDisplayOnly1: any = null;
  collectedUnpaidForDisplayOnly2: any = null;
  subtotalCollectedUnpaidForDisplayOnlyTaxincAmount1 = 0;
  subtotalCollectedUnpaidForDisplayOnlyTaxAmount1 = 0;
  subtotalCollectedUnpaidForDisplayOnlyCount1 = 0;
  subtotalCollectedUnpaidForDisplayOnlyTaxincAmount2 = 0;
  subtotalCollectedUnpaidForDisplayOnlyTaxAmount2 = 0;
  subtotalCollectedUnpaidForDisplayOnlyCount2 = 0;

  // --- bank transfer income totals (method=1) ---
  totalCount11 = 0;
  totalTaxincAmount11 = 0;
  totalTaxAmount11 = 0;

  // --- credit card income totals (method=2) ---
  totalCount12 = 0;
  totalTaxincAmount12 = 0;
  totalTaxAmount12 = 0;

  // --- type 1 income totals ---
  totalTaxincAmount1 = 0;
  totalTaxAmount1 = 0;

  // --- type 2 income ---
  totalTaxincAmount2 = 0;
  totalTaxAmount2 = 0;

  // --- type 3 taxed expense ---
  totalTaxincAmount3Per8 = 0;
  totalTaxAmount3Per8 = 0;
  totalTaxincAmount3Per10 = 0;
  totalTaxAmount3Per10 = 0;

  // --- type 4 non-taxed expense ---
  totalTaxexcAmount4 = 0;
  totalTaxAmount4 = 0;

  // --- type 5 advances ---
  totalAdvancesTaxincAmountPar8 = 0;
  totalAdvancesTaxAmountPar8 = 0;
  totalAdvancesTaxincAmountPar10 = 0;
  totalAdvancesTaxAmountPar10 = 0;

  totalTaxincAmount1Per10 = 0;
  totalTaxincAmount1Per8 = 0;
  totalTaxAmount1Per10 = 0;
  totalTaxAmount1Per8 = 0;
  totalPaymentTaxincAmountPer10 = 0;
  totalPaymentTaxincAmountPer8 = 0;
  totalPaymentTaxAmountPer10 = 0;
  totalPaymentTaxAmountPer8 = 0;
  paymentZeroAdjustmentAmount = 0;
  paymentZeroAdjustmentTaxAmount = 0;

  // --- summary ---
  totalExpenseTaxincAmount = 0;
  totalExpenseTaxAmount = 0;
  totalPaymentTaxincAmount = 0;
  totalPaymentTaxAmount = 0;

  // --- bank transfer subtotals ---
  subtotalPaymentCount1 = 0;
  subtotalPaymentAmount1 = 0;
  subtotalPaymentTaxAmount1 = 0;
  subtotalCollectedUnpaidCount1 = 0;
  subtotalCollectedUnpaidAmount1 = 0;
  subtotalCollectedUnpaidTaxAmount1 = 0;
  subtotalUnpaidCount1 = 0;
  subtotalUnpaidAmount1 = 0;
  subtotalUnpaidTaxAmount1 = 0;

  // --- credit card subtotals ---
  subtotalPaymentCount2 = 0;
  subtotalPaymentAmount2 = 0;
  subtotalPaymentTaxAmount2 = 0;
  subtotalCollectedUnpaidCount2 = 0;
  subtotalCollectedUnpaidAmount2 = 0;
  subtotalCollectedUnpaidTaxAmount2 = 0;
  subtotalUnpaidCount2 = 0;
  subtotalUnpaidAmount2 = 0;
  subtotalUnpaidTaxAmount2 = 0;

  // --- cash subtotals ---
  subtotalCashPaymentCount1 = 0;
  subtotalCashPaymentAmount1 = 0;
  subtotalCashPaymentTaxAmount1 = 0;
  subtotalCashPaymentCount2 = 0;
  subtotalCashPaymentAmount2 = 0;
  subtotalCashPaymentTaxAmount2 = 0;
  subtotalCashCollectedUnpaidCount1 = 0;
  subtotalCashCollectedUnpaidAmount1 = 0;
  subtotalCashCollectedUnpaidTaxAmount1 = 0;
  subtotalCashCollectedUnpaidCount2 = 0;
  subtotalCashCollectedUnpaidAmount2 = 0;
  subtotalCashCollectedUnpaidTaxAmount2 = 0;

  // --- aggregated rows ---
  aggregateDataOfPaymentAndCollectedUnpaid: any[] = [];
  aggregateDataOfUnpaid: any[] = [];

  // --- Modal flags ---
  showUpdateSettlementModal = false;
  showUpsertBalanceModal = false;
  showUpdateBalanceStatusModal = false;
  showBalanceDetailModal = false;
  showBalanceModal = false;
  showEditPayDateModal = false;
  disableBalanceDetailDownload = false;
  isDownloadingBalancePdf = false;
  isDownloadingBalanceDetailPdf = false;
  isPreparingBalanceModal = false;
  hasBalanceDetailData = false;
  balanceDetailLoadError = '';
  balanceDetailDownloadError = '';

  // --- Modal data state ---
  settlementPublishedAt = '';
  settlementStatus = '0';
  singleBalance: any = undefined;
  singleBalances: any[] = [];
  isEdit = false;
  isIncome = false;
  isAddIncome = false;
  isIncludeTax = false;
  settlementPayDate = '';

  // --- PDF detail state ---
  settleForPdfTotal: any[][] = [];
  settleForPdfThisMonth: any[][] = [];
  settleForPdfContinued: any[][] = [];
  thisIssueMonthBt: Date | null = null;
  thisIssueMonthCc: Date | null = null;
  continuedIssueMonthBt: Date | null = null;
  continuedIssueMonthCc: Date | null = null;

  get userPermission(): number {
    return this.authState.currentUser?.role ?? 99;
  }

  get periodMonth(): string {
    return `${this.selectedYear}-${('0' + this.selectedMonth).slice(-2)}`;
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('決済管理', 's-icon-settlement');
    this.studios = await this.studioService.list('all') ?? [];
    const taxData = await this.taxRateService.get('all') ?? [];
    this.taxRates = taxData.map((v: any) => ({
      id: v.id,
      name: `${v.rate * 100}%`,
      rate: v.rate,
    }));
    this.studioPlanTypeOptions = await this.studioService.getPlanType() ?? [];

    const restored = this.studioSelection.restore(this.studios, SESSION_KEY);
    if (restored) {
      this.selectedStudio = restored;
      await this.loadBalance();
    }
  }

  async onStudioChange(): Promise<void> {
    this.showSettlement = false;
    this.showBalance = false;
    this.balances = [];
    if (!this.selectedStudio) {
      this.studioSelection.clear(SESSION_KEY);
      return;
    }
    this.studioSelection.save(this.selectedStudio, SESSION_KEY);
    await this.loadBalance();
  }

  async loadBalance(): Promise<void> {
    if (!this.selectedStudio) return;
    this.now = new JstDate();

    const [data, studioData] = await Promise.all([
      this.studioService.getBalance(this.selectedStudio.id, this.selectedYear, this.selectedMonth),
      this.studioService.list(this.selectedStudio.id),
    ]);
    this.currentStudio = studioData;

    this.settlement = {};
    this.balances = [];

    if (data && data.length > 0 && data[0].settlement) {
      this.showSettlement = true;
      this.showBalance = true;
      this.settlement = data[0].settlement;
      Object.assign(this, summarizeBalance(data));
    } else {
      this.showSettlement = false;
      this.showBalance = false;
    }
  }

  // --- Settlement methods ---
  async saveSettlement(settlementId: number, newDate: string, newStatus: string): Promise<void> {
    if (!newDate) return;
    const publishedAt = newDate.includes('T')
      ? newDate
      : new JstDate(`${newDate}T00:00:00`).toISOString();
    const json = {
      id: settlementId,
      status: parseInt(newStatus, 10),
      publishedAt,
    };
    await this.balanceService.updateSettlement(json);
    await this.loadBalance();
  }

  async submitSettlementUpdate(): Promise<void> {
    await this.saveSettlement(this.settlement.id, this.settlementPublishedAt, this.settlementStatus);
    this.closeUpdateSettlementModal();
  }

  async updatePayDate(settleMonth: string, newDate: string): Promise<void> {
    if (!newDate) return;
    let payDateStr = newDate;
    // If newDate is a Date object, format it; otherwise use as-is
    if (newDate && typeof newDate !== 'string') {
      const d = new JstDate(newDate);
      payDateStr = `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`;
    }
    const json = { settleMonth, payDate: payDateStr };
    await this.balanceService.updateSettlementPayDate(json);
    await this.loadBalance();
  }

  // --- Balance CRUD ---
  async editSingleBalance(balance: any, isIncludeTax: boolean): Promise<void> {
    this.isAddIncome = false;
    this.singleBalances = [];
    this.singleBalance = undefined;
    this.isIncludeTax = isIncludeTax;
    // The old PJ starts with isEdit=false and only flips it after the edit data loads.
    this.isEdit = false;
    this.isIncome = false;

    if (!balance.id) return;

    const settlementId = balance.settlement?.id ?? this.settlement?.id;
    const method = balance.method;
    const chargeType = balance.chargeType;
    const type = balance.type;
    const subtype = balance.subtype;

    const data = await this.balanceService.getWithoutAggregation(
      settlementId, method, chargeType, type, subtype
    );

    let filtered: any[];
    if (!chargeType && chargeType !== 0) {
      if (Array.isArray(balance.id)) {
        filtered = data.filter((v: any) => (balance.id as number[]).includes(v.id));
      } else {
        filtered = data.filter((v: any) => balance.id === v.id);
      }
    } else {
      filtered = data.filter((v: any) => v.count || v.taxAmount || v.taxIncAmount);
      if (filtered.length === 0) return;
    }

    // Add select label
    filtered.forEach((v: any) => {
      v.selectName = getChargeTypeName(v.chargeType) + ' ' + v.name + ' '
        + (v.studioPlanType ? v.studioPlanType.name + ' ' : '')
        + ' ' + v.count + '件 ¥' + (v.taxIncAmount as number).toLocaleString();
    });

    // Remove taxAmount for editable types
    if (type === 1 || type === 2 || type === 3 || type === 5) {
      filtered = filtered.map(({ taxAmount, ...rest }: any) => rest);
    }

    this.singleBalances = filtered;
    this.singleBalance = this.singleBalances.length === 1 ? this.singleBalances[0] : undefined;
    this.isEdit = true;
  }

  addSingleBalance(typeNumber: number, isIncludeTax: boolean, method: number | null = null): void {
    this.singleBalance = {
      studioId: this.currentStudio?.id,
      settlementId: this.settlement?.id,
      sortBy: 99,
      isAutoGenerate: 1,
      status: 0,
    };
    this.isEdit = false;
    this.isIncome = false;
    this.isAddIncome = false;
    this.isIncludeTax = isIncludeTax;

    if (typeNumber === 1) {
      this.singleBalance.type = 1;
      this.singleBalance.subtype = 11;
      this.singleBalance.chargeType = 0;
      this.isIncome = true;
      this.isAddIncome = true;
    } else if (typeNumber === 2) {
      this.singleBalance.type = 1;
      this.singleBalance.subtype = 13;
      this.singleBalance.chargeType = 0;
      this.isIncome = true;
      this.isAddIncome = true;
    } else if (typeNumber === 3) {
      this.singleBalance.type = 3;
      this.singleBalance.subtype = 30;
    } else if (typeNumber === 4) {
      this.singleBalance.type = 4;
      this.singleBalance.subtype = 40;
    } else if (typeNumber === 5) {
      this.singleBalance.type = 5;
      this.singleBalance.subtype = 50;
      this.singleBalance.isInvoiceRegistrar = 0;
    } else if (typeNumber === 99) {
      this.singleBalance.type = 99;
      this.singleBalance.subtype = 99;
      this.singleBalance.name = '未入金回収';
      this.singleBalance.method = method;
    }
    if (this.singleBalance.type === 1) {
      this.singleBalance.taxRateId = this.taxRates.find(rate => Number(rate.rate) === 0.1)?.id;
    }
  }

  async saveSingleBalance(balance: any, isIncludeTax: boolean): Promise<void> {
    if (this.isSingleBalanceInvalid()) return;
    const json: any = { ...balance };
    if (isIncludeTax) {
      const taxRate = this.taxRates.find((v: any) => v.id === balance.taxRateId);
      json.taxIncAmount = parseInt(String(balance.taxIncAmount), 10);
      json.taxAmount = balance.type !== 1 && balance.taxAmount
        ? parseInt(String(balance.taxAmount), 10)
        : getTaxAmount(json.taxIncAmount, parseFloat(taxRate?.rate || 0.1));
      json.taxExcAmount = json.taxIncAmount - json.taxAmount;
    } else {
      json.taxIncAmount = parseInt(String(balance.taxExcAmount), 10);
      json.taxAmount = 0;
      json.taxExcAmount = parseInt(String(balance.taxExcAmount), 10);
    }
    if (json.subtype === 50) {
      json.isInvoiceRegistrar = json.isInvoiceRegistrar ? 1 : 0;
    }
    if (!json.id && json.method) {
      const m = Number(json.method);
      if (m === 1) {
        json.orgIssueMonth = `${this.selectedYear}-${('0' + this.selectedMonth).slice(-2)}`;
      } else {
        const prevMonth = this.selectedMonth === 1 ? 12 : this.selectedMonth - 1;
        const prevYear = this.selectedMonth === 1 ? this.selectedYear - 1 : this.selectedYear;
        json.orgIssueMonth = `${prevYear}-${('0' + prevMonth).slice(-2)}`;
      }
    }
    if (Number(json.chargeType) !== 1) {
      delete json['studioPlanTypeId'];
    }
    await this.balanceService.upsert(json);
    await this.loadBalance();
  }

  isSingleBalanceInputDisabled(): boolean {
    return !this.singleBalance || (this.isEdit && !this.singleBalance.id);
  }

  isSingleBalanceInvalid(): boolean {
    const balance = this.singleBalance;
    if (!balance) return true;
    if (this.isEdit && !balance.id) return true;
    if (!String(balance.name ?? '').trim()) return true;
    if (this.isAddIncome && this.isBlank(balance.chargeType)) return true;
    if (this.isIncome && this.isBlank(balance.method)) return true;
    if (balance.subtype !== 50 && !this.isIntegerLike(balance.count)) return true;
    if (this.isIncludeTax && !this.isFiniteNumberLike(balance.taxIncAmount)) return true;
    if (!this.isIncludeTax && !this.isFiniteNumberLike(balance.taxExcAmount)) return true;
    if ((balance.type === 1 || balance.type === 3 || balance.subtype === 50) && this.isBlank(balance.taxRateId)) return true;
    return false;
  }

  private isBlank(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private isIntegerLike(value: any): boolean {
    if (this.isBlank(value)) return false;
    return /^-?\d+$/.test(String(value));
  }

  private isFiniteNumberLike(value: any): boolean {
    if (this.isBlank(value)) return false;
    return Number.isFinite(Number(value));
  }

  async removeBalance(balance: any): Promise<void> {
    if (!balance?.id) return;
    await this.balanceService.remove(balance.id);
    await this.loadBalance();
  }

  async downloadBalanceAsPdf(): Promise<void> {
    if (this.isDownloadingBalancePdf) return;
    const fileName = `会費精算報告書_${this.currentStudio?.owner?.name}_${this.selectedYear}${('0' + this.selectedMonth).slice(-2)}.pdf`;
    const el1 = document.getElementById('balanceParts1') as HTMLElement;
    const el2 = document.getElementById('balanceParts2') as HTMLElement;
    if (!el1 || !el2) return;
    this.isDownloadingBalancePdf = true;
    this.closeShowBalanceModal();
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

    const root = document.documentElement;
    const originalFontSize = getComputedStyle(root).fontSize;
    const originalScrollX = window.scrollX;
    const originalScrollY = window.scrollY;
    root.style.fontSize = '10px';

    const applyStyle = (el: HTMLElement) => {
      el.style.fontFamily = 'Yu Gothic, Hiragino Sans, Helvetica, Arial, sans-serif';
      el.style.fontSize = '10px';
      el.style.minWidth = '780px';
      el.style.maxWidth = '780px';
      el.style.lineHeight = '1';
      el.style.margin = '0';
      el.style.padding = '0';
    };
    applyStyle(el1);
    applyStyle(el2);

    const h5Tags = [
      ...Array.from(el1.querySelectorAll<HTMLElement>('h5')),
      ...Array.from(el2.querySelectorAll<HTMLElement>('h5')),
    ];
    h5Tags.forEach(h5 => {
      h5.style.fontSize = '10px';
      h5.style.margin = '0';
      h5.style.padding = '0';
      h5.style.fontFamily = 'Yu Gothic, Hiragino Sans, Helvetica, Arial, sans-serif';
    });
    const cardBodies = [
      ...Array.from(el1.querySelectorAll<HTMLElement>('.card-body')),
      ...Array.from(el2.querySelectorAll<HTMLElement>('.card-body')),
    ];
    cardBodies.forEach(card => {
      card.style.padding = '10px';
      card.style.fontFamily = 'Yu Gothic, Hiragino Sans, Helvetica, Arial, sans-serif';
    });

    const clearStyle = (el: HTMLElement) => {
      el.style.fontSize = '';
      el.style.minWidth = '';
      el.style.maxWidth = '';
      el.style.lineHeight = '';
      el.style.margin = '';
      el.style.padding = '';
    };
    try {
      const pdf = new jsPDF('p', 'pt', 'a4', true);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20;

      window.scrollTo(0, 0);
      const canvas1 = await html2canvas(el1, { scale: 2, logging: false, useCORS: true });
      pdf.addImage(canvas1.toDataURL('image/png'), 'PNG', 10, 10, canvas1.width * (pdfWidth / canvas1.width), canvas1.height * (pdfWidth / canvas1.width), '', 'FAST');

      window.scrollTo(0, 0);
      const canvas2 = await html2canvas(el2, { scale: 2, logging: false, useCORS: true });
      pdf.addPage();
      pdf.addImage(canvas2.toDataURL('image/png'), 'PNG', 10, 10, canvas2.width * (pdfWidth / canvas2.width), canvas2.height * (pdfWidth / canvas2.width), '', 'FAST');

      pdf.save(fileName);
    } finally {
      root.style.fontSize = originalFontSize;
      clearStyle(el1);
      clearStyle(el2);
      h5Tags.forEach(h5 => { h5.style.fontSize = ''; h5.style.margin = ''; h5.style.padding = ''; h5.style.fontFamily = ''; });
      cardBodies.forEach(card => { card.style.padding = ''; card.style.fontFamily = ''; });
      window.scrollTo(originalScrollX, originalScrollY);
      this.isDownloadingBalancePdf = false;
    }
  }

  async downloadBalanceDetailAsPdf(): Promise<void> {
    if (this.isDownloadingBalanceDetailPdf || this.disableBalanceDetailDownload) return;
    const wrapElement = document.getElementById('showBalanceDetailWrap') as HTMLElement | null;
    const element = document.getElementById('showBalanceDetail') as HTMLElement;
    if (!element) return;
    this.isDownloadingBalanceDetailPdf = true;
    this.balanceDetailDownloadError = '';
    this.closeBalanceDetailModal();
    const previousWrapStyle = wrapElement?.getAttribute('style');
    const previousElementStyle = element.getAttribute('style');
    let cleanupPages = () => {};
    element.style.display = 'block';
    element.style.transform = 'none';
    element.style.height = 'auto';
    if (wrapElement) {
      wrapElement.style.display = 'block';
      wrapElement.style.position = 'fixed';
      wrapElement.style.left = '-10000px';
      wrapElement.style.top = '0';
      wrapElement.style.zIndex = '-1';
    }
    try {
      const pdf = new jsPDF({ unit: 'in', format: 'a4', orientation: 'landscape' });
      const margin = 0.5;
      const width = pdf.internal.pageSize.getWidth() - margin * 2;
      const height = pdf.internal.pageSize.getHeight() - margin * 2;
      const prepared = prepareDetailPdfPages(element, height / width);
      cleanupPages = prepared.cleanup;
      const pages = prepared.pages;
      for (const [index, page] of pages.entries()) {
        const canvas = await html2canvas(page, { scale: 4, backgroundColor: '#ffffff' });
        const ratio = Math.min(width / canvas.width, height / canvas.height);
        if (index > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', margin, margin, canvas.width * ratio, canvas.height * ratio);
      }
      pdf.save(`${this.currentStudio?.name}_会費精算書内訳明細.pdf`);
    } catch {
      this.balanceDetailDownloadError = 'PDFの作成に失敗しました。会費精算書内訳明細ダウンロードから再度お試しください。';
    } finally {
      cleanupPages();
      if (previousElementStyle === null) element.removeAttribute('style');
      else element.setAttribute('style', previousElementStyle);
      if (wrapElement) {
        if (previousWrapStyle == null) wrapElement.removeAttribute('style');
        else wrapElement.setAttribute('style', previousWrapStyle);
      }
      this.isDownloadingBalanceDetailPdf = false;
    }
  }

  async getDetail(): Promise<void> {
    if (!this.selectedStudio || this.disableBalanceDetailDownload || this.showBalanceDetailModal) return;
    this.disableBalanceDetailDownload = true;
    this.hasBalanceDetailData = false;
    this.balanceDetailLoadError = '';
    this.showBalanceDetailModal = true;
    const month = this.periodMonth;
    try {
      const data = await this.balanceService.getDetail(this.selectedStudio.id, month);
      this.hasBalanceDetailData = (data ?? []).some((row: any) => {
        const count = Number(row?.count);
        return Number.isFinite(count) && count !== 0;
      });
      this.now = new JstDate();
      this.thisIssueMonthBt = new JstDate(this.selectedYear, this.selectedMonth, 1);
      this.thisIssueMonthCc = new JstDate(this.selectedYear, this.selectedMonth - 1, 1);
      this.continuedIssueMonthBt = new JstDate(this.selectedYear, this.selectedMonth - 1, 1);
      this.continuedIssueMonthCc = new JstDate(this.selectedYear, this.selectedMonth - 2, 1);
      [this.settleForPdfTotal, this.settleForPdfThisMonth, this.settleForPdfContinued] =
        createSettlesForPdf(this.selectedYear, this.selectedMonth, data ?? []);
    } catch {
      this.balanceDetailLoadError = '内訳データの取得に失敗しました。閉じてから再度お試しください。';
    } finally {
      this.disableBalanceDetailDownload = false;
    }
  }

  getChargeTypeName(chargeType: number): string {
    return getChargeTypeName(chargeType);
  }

  // --- Modal openers/closers ---
  openUpdateSettlementModal(): void {
    this.settlementPublishedAt = this.toDateInputValue(this.settlement?.publishedAt);
    this.settlementStatus = String(this.settlement?.status ?? '0');
    this.showUpdateSettlementModal = true;
  }

  closeUpdateSettlementModal(): void {
    this.showUpdateSettlementModal = false;
  }

  openUpsertBalanceModal(): void {
    this.showUpsertBalanceModal = true;
  }

  closeUpsertBalanceModal(): void {
    this.showUpsertBalanceModal = false;
  }

  openDeleteBalanceModal(balance: any): void {
    this.singleBalance = balance;
    this.showUpdateBalanceStatusModal = true;
  }

  closeUpdateBalanceStatusModal(): void {
    this.showUpdateBalanceStatusModal = false;
  }

  closeBalanceDetailModal(): void {
    this.showBalanceDetailModal = false;
  }

  openShowBalanceModal(): void {
    if (this.isPreparingBalanceModal || this.showBalanceModal) return;
    this.isPreparingBalanceModal = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isPreparingBalanceModal) return;
        this.showBalanceModal = true;
        this.isPreparingBalanceModal = false;
      });
    });
  }

  closeShowBalanceModal(): void {
    this.isPreparingBalanceModal = false;
    this.showBalanceModal = false;
  }

  openEditPayDateModal(): void {
    this.settlementPayDate = this.toDateInputValue(this.settlement?.payDate);
    this.showEditPayDateModal = true;
  }

  closeEditPayDateModal(): void {
    this.showEditPayDateModal = false;
  }

  private toDateInputValue(value: any): string {
    if (!value) return '';
    // DATEONLY values stay unchanged; timestamps must use the local calendar date,
    // matching the local midnight converted to UTC by saveSettlement().
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = new JstDate(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;
  }
}
